import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { Lookalike, Species } from '../../domain/regs-models';

@Component({
  selector: 'app-keep-it-id-page',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="id-page">
      <div class="id-header">
        <button type="button" class="icon-circle-btn" (click)="back.emit()" aria-label="Back">
          <span class="material-icons">chevron_left</span>
        </button>
        <div class="id-header-text">
          <h2 class="id-title">How to tell it</h2>
          <p class="id-sub">{{ species.short }}{{ species.scientific ? ' · ' + species.scientific : '' }}</p>
        </div>
        <button type="button" class="icon-circle-btn" (click)="close.emit()" aria-label="Close">
          <span class="material-icons">close</span>
        </button>
      </div>

      <div class="id-content">
        <!-- Question bubble -->
        <div class="user-bubble-wrap">
          <div class="user-bubble">
            How do I tell {{ article(species.short) }} {{ species.short.toLowerCase() }} from {{ article(focus.name) }} {{ focus.name.toLowerCase() }}?
          </div>
        </div>

        <!-- Skipper bubble -->
        <div class="skipper-says-row">
          <div class="skipper-avatar-circle">
            <img src="/assets/common_icons/vectormarine.svg" alt="Skipper" />
          </div>
          <div class="skipper-bubble">
            {{ tellText }}
          </div>
        </div>

        <!-- Spotting notes -->
        @if (notes.length > 0) {
          <div class="card-box">
            <div class="section-label">How to spot {{ article(species.short) }} {{ species.short.toLowerCase() }}</div>
            <div class="checklist">
              @for (n of notes; track $index) {
                <div class="check-item">
                  <span class="material-icons check-ico">check</span>
                  <span class="check-text">{{ n }}</span>
                </div>
              }
            </div>
          </div>
        }

        <!-- Other lookalikes -->
        @for (l of otherLookalikes; track l.name) {
          <div class="card-box" style="margin-top: 8px;">
            <div class="card-title">Also not to be confused with {{ l.name.toLowerCase() }}</div>
            <div class="card-desc">{{ l.tell }}</div>
          </div>
        }
      </div>
    </div>
  `,
  styles: [
    `
      .id-page {
        display: flex;
        flex-direction: column;
        height: 100%;
        background: #ffffff;
      }
      .id-header {
        display: flex;
        align-items: center;
        padding: 12px 16px;
        border-bottom: 1px solid #ededf0;
      }
      .id-header-text {
        flex: 1;
        margin: 0 10px;
      }
      .id-title {
        font-size: 17px;
        font-weight: 600;
        color: #1c1c1e;
        margin: 0;
      }
      .id-sub {
        font-size: 12.5px;
        color: #8a8a8e;
        margin: 2px 0 0;
      }
      .icon-circle-btn {
        width: 34px;
        height: 34px;
        border-radius: 50%;
        border: none;
        background: #f2f2f6;
        color: #3a3a3c;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
      }
      .icon-circle-btn .material-icons {
        font-size: 20px;
      }
      .id-content {
        flex: 1;
        overflow-y: auto;
        padding: 16px;
      }
      .user-bubble-wrap {
        display: flex;
        justify-content: flex-end;
        margin-bottom: 14px;
      }
      .user-bubble {
        max-width: 82%;
        padding: 10px 15px;
        background: #2463eb;
        color: #ffffff;
        font-size: 15px;
        line-height: 1.4;
        border-radius: 18px 18px 6px 18px;
      }
      .skipper-says-row {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        margin-bottom: 16px;
      }
      .skipper-avatar-circle {
        width: 32px;
        height: 32px;
        border-radius: 50%;
        background: #eaf2fe;
        display: flex;
        align-items: center;
        justify-content: center;
        flex: none;
        overflow: hidden;
      }
      .skipper-avatar-circle img {
        width: 20px;
        height: 20px;
      }
      .skipper-bubble {
        flex: 1;
        padding: 12px 15px;
        background: #eef5fe;
        border: 1px solid #d9e7fc;
        border-radius: 18px;
        font-size: 15px;
        color: #2c2c2e;
        line-height: 1.38;
      }
      .card-box {
        background: #ffffff;
        border: 1px solid #e5e5ea;
        border-radius: 16px;
        padding: 16px;
      }
      .section-label {
        font-size: 12px;
        font-weight: 600;
        color: #8a8a8e;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 10px;
      }
      .checklist {
        display: flex;
        flex-direction: column;
        gap: 10px;
      }
      .check-item {
        display: flex;
        align-items: flex-start;
        gap: 10px;
      }
      .check-ico {
        color: #1f9d61;
        font-size: 20px;
        flex: none;
      }
      .check-text {
        font-size: 14.5px;
        color: #1c1c1e;
        line-height: 1.4;
      }
      .card-title {
        font-size: 15.5px;
        font-weight: 600;
        color: #1c1c1e;
      }
      .card-desc {
        font-size: 14.5px;
        color: #1c1c1e;
        line-height: 1.4;
        margin-top: 4px;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KeepItIdPageComponent {
  @Input({ required: true }) species!: Species;
  @Input() focus!: Lookalike;
  @Input() set lookalike(l: Lookalike) {
    this.focus = l;
  }

  @Output() back = new EventEmitter<void>();
  @Output() close = new EventEmitter<void>();

  article(noun: string): string {
    const first = noun.trim().toLowerCase()[0] || 'x';
    return 'aeiou'.includes(first) ? 'an' : 'a';
  }

  get tellText(): string {
    const t = this.focus.tell.trim();
    if (t.length === 0) {
      return 'I don’t have a reliable way to tell them apart in my notes. If you’re not sure, check the official source before you keep it.';
    }
    return t.endsWith('.') ? t : `${t}.`;
  }

  get notes(): string[] {
    return this.species.realIdNotes;
  }

  get otherLookalikes(): Lookalike[] {
    return this.species.lookalikes.filter((l) => l.name !== this.focus.name);
  }
}
